import type { FC } from 'react'
import type { TokenKind } from '@/lib/highlight'

import { useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { cn } from '@/lib/utils'
import { tokenizeLine } from '@/lib/highlight'

export type Snippet = {
    id: string
    file: string
    code: string
}

interface CodeWindowProps {
    snippets: Snippet[]
    note?: string
}

const TOKEN_STYLES: Record<TokenKind, string> = {
    plain: 'text-[#d8d5cf]',
    comment: 'italic text-[#6e6c68]',
    string: 'text-[#e6d5ae]',
    tag: 'text-[#7fe0b5]',
    keyword: 'text-ember-soft',
    number: 'text-[#c9b6ff]',
    function: 'text-bone',
    attr: 'text-[#ffb08a]',
    property: 'text-[#b9b6b0]'
}

const CodeWindow: FC<CodeWindowProps> = ({ snippets, note }) => {
    const [activeId, setActiveId] = useState(snippets[0]?.id ?? '')
    const [copied, setCopied] = useState(false)
    const active = snippets.find(snippet => snippet.id === activeId) ?? snippets[0]

    if (!active) return null

    const lines = active.code.split('\n')

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(active.code)
            setCopied(true)
            window.setTimeout(() => setCopied(false), 1600)
        }
        catch {
            setCopied(false)
        }
    }

    return (
        <div className='overflow-hidden rounded-[28px] border border-white/10 bg-[#0b0b0d] shadow-[0_60px_140px_-70px_rgba(255,91,31,0.4)]'>
            <div className='flex h-12 items-center justify-between gap-3 border-b border-white/[0.06] pl-4 pr-2'>
                <div className='flex items-center gap-4'>
                    <div aria-hidden='true' className='hidden gap-1.5 sm:flex'>
                        <span className='size-2.5 rounded-full bg-white/12' />
                        <span className='size-2.5 rounded-full bg-white/12' />
                        <span className='size-2.5 rounded-full bg-white/12' />
                    </div>

                    <div role='tablist' aria-label='Code examples' className='flex gap-1'>
                        {snippets.map(snippet => (
                            <button
                                key={snippet.id}
                                type='button'
                                role='tab'
                                id={`tab-${snippet.id}`}
                                aria-selected={snippet.id === active.id}
                                aria-controls={`panel-${snippet.id}`}
                                onClick={() => setActiveId(snippet.id)}
                                className={cn(
                                    'h-8 rounded-lg px-3 font-mono text-[11px] transition-colors',
                                    snippet.id === active.id ? 'bg-white/[0.07] text-bone' : 'text-steel hover:text-bone'
                                )}
                            >
                                {snippet.file}
                            </button>
                        ))}
                    </div>
                </div>

                <button
                    type='button'
                    onClick={copy}
                    className='inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 font-mono text-[10px] uppercase tracking-[0.14em] text-steel transition-colors hover:bg-white/[0.06] hover:text-bone'
                >
                    {copied ? <Check aria-hidden='true' className='size-3.5 text-mint' /> : <Copy aria-hidden='true' className='size-3.5' />}
                    {copied ? 'Copied' : 'Copy'}
                </button>
            </div>

            <div role='tabpanel' id={`panel-${active.id}`} aria-labelledby={`tab-${active.id}`} className='overflow-x-auto'>
                <pre className='min-w-max py-5 pr-6 font-mono text-[11.5px] leading-[1.85]'>
                    <code>
                        {lines.map((line, index) => (
                            <span key={`${active.id}-${index}`} className='grid grid-cols-[3rem_1fr]'>
                                <span aria-hidden='true' className='select-none pr-4 text-right text-white/20'>
                                    {index + 1}
                                </span>
                                <span>
                                    {line.length === 0
                                        ? ' '
                                        : tokenizeLine(line).map((token, tokenIndex) => (
                                            <span key={tokenIndex} className={TOKEN_STYLES[token.kind]}>
                                                {token.text}
                                            </span>
                                        ))}
                                </span>
                            </span>
                        ))}
                    </code>
                </pre>
            </div>

            {note && (
                <div className='border-t border-white/[0.06] px-5 py-3.5 font-mono text-[10px] leading-relaxed text-steel'>
                    {note}
                </div>
            )}
        </div>
    )
}

export default CodeWindow
